import React, { useState, useEffect, useRef, useMemo, useCallback, memo } from 'react';
import { FieldArray, useFormikContext } from 'formik';
import { Row, Col, message, Card, Switch, Tooltip, Tag } from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import { setIsRefetch, validateEmailThunk, reVerifyEmailThunk, updateContactVerificationThunk } from '../../../Slices/salesSlice';
import axios from 'axios';
import { SERVER_URL } from '../../../databucket';
import Swal from 'sweetalert2';
import { InputField, AntdButton, TextareaField } from '../../../blueplanitUtils/InputField';
import { normalizePhone, phoneToDial, formatPhoneAsYouType, caretAfterDigits, PHONE_PLACEHOLDER } from '../../../utils/phone';
import { setLoader } from '../../../Slices/loaderSlice';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faBan } from "@fortawesome/free-solid-svg-icons";
import { manualUnsubscribe } from '../../../Slices/bulkOutreachSlice';
import { Line } from 'recharts';
import CommentField from './CommentField';

const ContactRow = memo(({
  contact,
  index,
  errors,
  touched,
  localErrors,
  showSidePanel,
  validatedEmails,
  showValidateButton,
  hasRingCentralIframe,
  callingTrackRef,
  onContactPersonChange,
  onTitleChange,
  onPhoneChange,
  onPhoneBlur,
  onEmailChange,
  onRemarksChange,
  onValidateEmail,
  onReVerifyEmail,
  verifyingIndex,
  onDeleteRow,
  setLocalContacts,
  markRowAsEdited,
  onUnsubscribe,
}) => {
  return (
    <Card className="contact-card" bordered={false}>
      <i
        className={`fa-regular fa-trash-can delete-icon ${showSidePanel ? "small" : "top-right"}`}
        onClick={() => onDeleteRow(index)}
      />

      <Row gutter={16} key={index} className="contact-row">
        {/* CONTACT */}
        <Col span={showSidePanel ? 12 : 4}>
          <div className={`inline-field small-gap ${showSidePanel ? "show-sidepanel" : ""}`}>
            <span className="label-small">{`Contact ${index + 1}`}</span>
            <InputField
              value={contact?.contactPerson || ""}
              onChange={(e) => onContactPersonChange(e, index)}
              copyable
            />
          </div>
        </Col>

        {/* TITLE */}
        <Col span={showSidePanel ? 12 : 4}>
          <div className={`inline-field ${showSidePanel ? "show-sidepanel" : ""}`}>
            <span className="label-title">Title</span>
            <InputField
              label={null}
              value={contact?.title || ""}
              onChange={(e) => onTitleChange(e, index)}
              copyable
            />
          </div>
        </Col>

        {/* PHONE */}
        <Col span={showSidePanel ? 12 : 4}>
          {/* Only the ref object is checked here: CallingTrack mounts after these
              memoized rows, so ref.current is still empty on their first render
              and nothing re-renders them once it is set. Resolve it on click. */}
          {hasRingCentralIframe && callingTrackRef ? (
            <i
              className="fa-solid fa-phone phone-icon"
              title="Call"
              onClick={() => {
                const phoneToCall = phoneToDial(contact?.phone);
                if (!phoneToCall) {
                  message.warning("Add a phone number to call this contact.");
                  return;
                }
                const callingTrack = callingTrackRef.current;
                if (!callingTrack?.handleStartButtonClick) {
                  message.warning("Calling is still loading. Please try again in a moment.");
                  return;
                }
                callingTrack.handleStartButtonClick(callingTrack.arrayHelpers, phoneToCall);
              }}
            />
          ) : (
            <i
              className="fa-solid fa-phone phone-icon disabled"
              title="Call unavailable: RingCentral is not loaded. Sign in to the RingCentral widget to call."
            />
          )}

          <div
            className={`inline-field ${showSidePanel ? "show-sidepanel" : ""}`}
            style={{ marginTop: showSidePanel ? 4 : 2 }}
          >
            <span className="label-phone">Phone</span>
            <InputField
              label={null}
              value={contact?.phone || ""}
              placeholder={PHONE_PLACEHOLDER}
              onChange={(e) => onPhoneChange(e, index)}
              onBlur={() => onPhoneBlur(index)}
              copyable
            />
          </div>
        </Col>

        {/* EMAIL + OPT-IN */}
        <Col span={showSidePanel ? 12 : 6}>
          <div style={{ marginTop: showSidePanel ? 4 : 2 }}>
            {/* Email input row */}
            <div style={{ display: "flex", alignItems: "flex-start", gap: 6 }}>
              <span className="label-email" style={{ paddingTop: 6, flexShrink: 0 }}>Email</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <InputField
                  label={null}
                  value={contact?.email || ""}
                  onChange={(e) => onEmailChange(e, index)}
                  error={
                    localErrors[index]?.email ||
                    (touched?.leadsContacts?.[index]?.email &&
                      errors?.leadsContacts?.[index]?.email)
                  }
                  copyable
                />
                {/* Verification badge below email input */}
                <div style={{ marginTop: 3, display: 'flex', alignItems: 'center', gap: 4 }}>
                  {!(contact.verifiedAt || contact.status || contact.verifyResult || contact.verifyReason) && contact?.email?.trim() ? (() => {
                    const isVerifying = verifyingIndex === index;
                    const isDisabled = !!localErrors[index]?.email || !contact?.email?.trim() || isVerifying;
                    return (
                      <Tooltip title={isVerifying ? "Verifying..." : "Click to verify this email address"}>
                        <Tag
                          style={{
                            margin: 0, borderRadius: 12, fontSize: 10, fontWeight: 600,
                            lineHeight: '20px', padding: '0 10px',
                            cursor: isDisabled ? 'not-allowed' : 'pointer',
                            color: isDisabled ? '#a0aec0' : '#1d4ed8',
                            background: isDisabled ? '#f1f5f9' : '#eff6ff',
                            borderColor: isDisabled ? '#e2e8f0' : '#93c5fd',
                            pointerEvents: isDisabled ? 'none' : 'auto',
                            transition: 'all 0.2s',
                          }}
                          onClick={() => {
                            if (!isDisabled) onValidateEmail(contact.email.trim(), index);
                          }}
                        >
                          {isVerifying ? (
                            <><i className="fa-solid fa-spinner fa-spin" style={{ marginRight: 4 }} />Verifying...</>
                          ) : (
                            <><i className="fa-solid fa-shield-halved" style={{ marginRight: 4 }} />Verify</>
                          )}
                        </Tag>
                      </Tooltip>
                    );
                  })() : (contact.verifiedAt || contact.status || contact.verifyResult || contact.verifyReason) ? (() => {
                    const code = (contact.verifyCode || '').toLowerCase();
                    const STATUS_STYLES = {
                      valid:        { color: '#16a34a', bg: '#f0fdf4', border: '#86efac', icon: 'fa-circle-check',          label: 'valid' },
                      'catch-all':  { color: '#d97706', bg: '#fffbeb', border: '#fcd34d', icon: 'fa-triangle-exclamation',  label: 'catch-all' },
                      do_not_mail:  { color: '#ea580c', bg: '#fff7ed', border: '#fdba74', icon: 'fa-ban',                  label: 'do_not_mail' },
                      invalid:      { color: '#dc2626', bg: '#fef2f2', border: '#fca5a5', icon: 'fa-circle-xmark',         label: 'invalid' },
                      spamtrap:     { color: '#991b1b', bg: '#fef2f2', border: '#f87171', icon: 'fa-skull-crossbones',      label: 'spamtrap' },
                      abuse:        { color: '#991b1b', bg: '#fef2f2', border: '#f87171', icon: 'fa-shield-exclamation',    label: 'abuse' },
                      unknown:      { color: '#64748b', bg: '#f8fafc', border: '#cbd5e1', icon: 'fa-circle-question',       label: 'unknown' },
                    };
                    const style = contact.status ? STATUS_STYLES.valid : (STATUS_STYLES[code] || STATUS_STYLES.invalid);
                    const vDate = contact.verifiedAt ? new Date(contact.verifiedAt) : null;
                    const shortDate = vDate ? `${vDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })} ${vDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : '';
                    const tooltipLines = [
                      `Status: ${code || (contact.status ? 'valid' : 'invalid')}`,
                      contact.verifyReason ? `Reason: ${contact.verifyReason}` : '',
                      vDate ? `Verified: ${vDate.toLocaleString()}` : '',
                      contact.isStale ? 'Stale — click ↻ to re-verify' : '',
                    ].filter(Boolean).join('\n');
                    return (
                      <>
                        <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{tooltipLines}</span>}>
                          <div style={{
                            display: 'inline-flex', alignItems: 'center', gap: 4,
                            borderRadius: 20, padding: '1px 8px',
                            background: style.bg, border: `1.5px solid ${style.border}`,
                          }}>
                            <i className={`fa-solid ${style.icon}`} style={{ fontSize: 11, color: style.color }} />
                            <span style={{ fontSize: 10, fontWeight: 700, color: style.color, letterSpacing: 0.3 }}>{style.label}</span>
                            {shortDate && (
                              <span style={{ fontSize: 9, color: '#94a3b8', fontWeight: 400 }}>· {shortDate}</span>
                            )}
                          </div>
                        </Tooltip>
                        {contact.isStale && (
                          <Tooltip title="Re-verify (older than 30 days)">
                            <span
                              style={{ color: '#d97706', fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                              onClick={() => onReVerifyEmail(contact?.email?.trim(), index)}
                            >
                              <i className="fa-solid fa-arrow-rotate-right" />
                            </span>
                          </Tooltip>
                        )}
                      </>
                    );
                  })() : null}
                </div>
              </div>

              {/* Opt-in toggle — inline on full view, hidden here on sidepanel */}
              {!showSidePanel && (
                <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, paddingTop: 2 }}>
                  {contact.unsubscribedAt ? (
                    <Tooltip title={`Unsubscribed on ${new Date(contact.unsubscribedAt).toLocaleDateString()}`}>
                      <span style={{ fontSize: 10, fontWeight: 700, color: '#dc2626', whiteSpace: 'nowrap', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 10, padding: '1px 8px' }}>
                        Unsubscribed
                      </span>
                    </Tooltip>
                  ) : (
                    <>
                      <span style={{ fontSize: 10, fontWeight: 600, color: contact.optIn ? '#52c41a' : '#ff4d4f', whiteSpace: 'nowrap', lineHeight: 1.2 }}>
                        {contact.optIn ? 'Opted In' : 'Opted Out'}
                      </span>
                      <Switch
                        checked={!!contact.optIn}
                        size="small"
                        onChange={(checked) => {
                          setLocalContacts((prev) => {
                            const arr = [...prev];
                            arr[index] = { ...arr[index], optIn: checked };
                            return arr;
                          });
                          markRowAsEdited(index);
                        }}
                        style={{ background: contact.optIn ? '#52c41a' : '#ff4d4f' }}
                      />
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Opt-in toggle — sub-row below email on sidepanel */}
            {showSidePanel && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
                {contact.unsubscribedAt ? (
                  <Tooltip title={`Unsubscribed on ${new Date(contact.unsubscribedAt).toLocaleDateString()}`}>
                    <span style={{ fontSize: 10, fontWeight: 700, color: '#dc2626', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 10, padding: '1px 8px' }}>
                      Unsubscribed
                    </span>
                  </Tooltip>
                ) : (
                  <>
                    <Switch
                      checked={!!contact.optIn}
                      size="small"
                      onChange={(checked) => {
                        setLocalContacts((prev) => {
                          const arr = [...prev];
                          arr[index] = { ...arr[index], optIn: checked };
                          return arr;
                        });
                        markRowAsEdited(index);
                      }}
                      style={{ background: contact.optIn ? '#52c41a' : '#ff4d4f' }}
                    />
                    <span style={{ fontSize: 11, fontWeight: 600, color: contact.optIn ? '#52c41a' : '#ff4d4f' }}>
                      {contact.optIn ? 'Opted In' : 'Opted Out'}
                    </span>
                  </>
                )}
                {/* Manual Unsubscribe button — for when recipient asks via call/email */}
                {contact.email && !contact.unsubscribedAt && onUnsubscribe && (
                  <Tooltip title="Mark as unsubscribed — stops all campaigns & sequences for this contact">
                    <span
                      style={{ fontSize: 10, color: '#dc2626', cursor: 'pointer', marginLeft: 6, fontWeight: 600, whiteSpace: 'nowrap' }}
                      onClick={(e) => { e.stopPropagation(); onUnsubscribe(contact, index); }}
                    >
                      <FontAwesomeIcon icon={faBan} style={{ marginRight: 3 }} />Unsubscribe
                    </span>
                  </Tooltip>
                )}
              </div>
            )}
          {/* did_you_mean suggestion */}
          {contact.didYouMean && (
            <div style={{
              fontSize: 11, color: '#1d4ed8', marginTop: 3, paddingLeft: 42,
              cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4,
            }}
              onClick={() => {
                onEmailChange({ target: { value: contact.didYouMean } }, index);
                message.info(`Email changed to suggested: ${contact.didYouMean}`);
              }}
            >
              <i className="fa-solid fa-lightbulb" style={{ color: '#faad14', fontSize: 10 }} />
              Did you mean <strong>{contact.didYouMean}</strong>?
            </div>
          )}
          </div>
        </Col>

        {/* REMARKS */}
        <Col span={showSidePanel ? 24 : 6}>
          <div
            className={`remarks-container ${showSidePanel ? "show-sidepanel" : ""}`}
            style={{ display: "flex", alignItems: "center", gap: "8px"}}
          >
            <span className="label-remarks" style={{ marginTop:'-15px'}}>Remarks</span>
            <TextareaField
            autoSize= {{ minRows:1 , maxRows:3}}
              label={null}
              value={contact?.remarks || ""}
              onChange={(e) => onRemarksChange(e, index)}
              className={`contact-remarks-textarea ${showSidePanel ? "contact-remarks-sidepanel" : ""}`}
              style={{
                width: '100%',
                height:'32px',
                minHeight:'32px',
                padding : "4px 11px",
                lineHeight: '22px',
                boxSizing: 'border-box',
                resize:'vertical',
              }}
            />
          </div>
        </Col>
      </Row>
    </Card>
  );
});

ContactRow.displayName = 'ContactRow';

const ContactInformationForm = ({ handleAddRow, callingTrackRef, showSidePanel }) => {
  const { values, errors, touched, setFieldValue } = useFormikContext();
  const dispatch = useDispatch();
  const loginUser = useSelector((state) => state?.loginUserInfo?.loginUserDetails);
  const isRefetch = useSelector((state) => state.sales.isRefetch);

  const [localContacts, setLocalContacts] = useState([]);
  const [localErrors, setLocalErrors] = useState([]);
  const [validatedEmails, setValidatedEmails] = useState([]);
  const [showValidateButton, setShowValidateButton] = useState([]);

  const initializedRef = useRef(false);
  const userEditedIndicesRef = useRef(new Set());
  const previousBackendDataRef = useRef(JSON.stringify(values?.leadsContacts || []));
  const syncTimeoutRef = useRef(null);

  const [verifyingIndex, setVerifyingIndex] = useState(null); // index of contact currently being verified

  // Poll for the RC widget iframe — it mounts asynchronously after the
  // RingCentralProvider injects the adapter script, so a one-shot useMemo
  // at mount time can latch `false` and never recover.
  const [hasRingCentralIframe, setHasRingCentralIframe] = useState(
    () => typeof document !== 'undefined' && !!document.querySelector('#rc-widget-adapter-frame')
  );
  useEffect(() => {
    if (hasRingCentralIframe || typeof document === 'undefined') return;
    const interval = setInterval(() => {
      if (document.querySelector('#rc-widget-adapter-frame')) {
        setHasRingCentralIframe(true);
        clearInterval(interval);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [hasRingCentralIframe]);

  // Initial load from Formik values - only run ONCE
  useEffect(() => {
    if (!initializedRef.current && values?.leadsContacts) {
      const contacts = values.leadsContacts;
      setLocalContacts(contacts.map((c) => ({ ...c })));

      const initialValidated = contacts.map((c) => ((c.status || c.verifiedAt) ? c.email || '' : ''));
      setValidatedEmails(initialValidated);
      setShowValidateButton(
        contacts.map((c, i) => {
          const email = (c.email || '').trim();
          const validated = initialValidated[i] || '';
          return email !== validated;
        })
      );
      setLocalErrors(contacts.map((c) => (c.verifyReason && !c.status ? { email: c.verifyReason } : {})));
      previousBackendDataRef.current = JSON.stringify(contacts);
      initializedRef.current = true;
    }
  }, [values?.leadsContacts]);

  // Detect backend data changes and merge intelligently
  useEffect(() => {
    if (!initializedRef.current) return;

    const currentBackendData = JSON.stringify(values?.leadsContacts || []);

    // Check if backend data has actually changed
    if (currentBackendData !== previousBackendDataRef.current) {
      const backendContacts = values?.leadsContacts || [];

      setLocalContacts((prev) => {
        const merged = [];

        // For each position, decide whether to keep user edit or use new backend data
        for (let i = 0; i < Math.max(prev.length, backendContacts.length); i++) {
          const localContact = prev[i];
          const backendContact = backendContacts[i];
          const wasUserEdited = userEditedIndicesRef.current.has(i);

          if (wasUserEdited && localContact) {
            // User edited this row - keep user's data
            merged[i] = { ...localContact };
          } else if (backendContact) {
            // Backend data is new or user didn't edit - use backend
            merged[i] = { ...backendContact };
          } else if (localContact) {
            // Keep local if no backend equivalent
            merged[i] = { ...localContact };
          }
        }

        return merged;
      });

      previousBackendDataRef.current = currentBackendData;
    }
  }, [values?.leadsContacts]);

  // Helper to mark row as edited by user
  const markRowAsEdited = useCallback((index) => {
    userEditedIndicesRef.current.add(index);
  }, []);

  // ✅ OPTIMIZED: Individual row update handlers - minimal state changes
  const handleContactPersonChange = useCallback((e, index) => {
    markRowAsEdited(index);
    const value = e.target.value;
    setLocalContacts((prev) => {
      const arr = [...prev];
      arr[index] = { ...arr[index], contactPerson: value };
      return arr;
    });
  }, [markRowAsEdited]);

  const handleTitleChange = useCallback((e, index) => {
    markRowAsEdited(index);
    const value = e.target.value;
    setLocalContacts((prev) => {
      const arr = [...prev];
      arr[index] = { ...arr[index], title: value };
      return arr;
    });
  }, [markRowAsEdited]);

  // Formats while typing/pasting. Deletions are left alone so backspace never fights
  // the formatter; the field is normalised on blur anyway.
  const handlePhoneChange = useCallback((e, index) => {
    markRowAsEdited(index);
    const input = e.target;
    const raw = input.value;
    const isDeletion = e.nativeEvent?.inputType?.startsWith("delete");
    const value = isDeletion ? raw : formatPhoneAsYouType(raw);
    if (value !== raw && typeof input.setSelectionRange === "function") {
      const digitsBeforeCaret = raw.slice(0, input.selectionStart ?? raw.length).replace(/\D/g, "").length;
      const caret = (input.selectionStart ?? raw.length) >= raw.length
        ? value.length
        : caretAfterDigits(value, digitsBeforeCaret);
      requestAnimationFrame(() => {
        if (document.activeElement === input) input.setSelectionRange(caret, caret);
      });
    }
    setLocalContacts((prev) => {
      const arr = [...prev];
      arr[index] = { ...arr[index], phone: value };
      return arr;
    });
  }, [markRowAsEdited]);

  // Show the stored format as soon as the user leaves the field (the server
  // normalises again on save).
  const handlePhoneBlur = useCallback((index) => {
    setLocalContacts((prev) => {
      const current = prev[index]?.phone;
      const formatted = normalizePhone(current);
      if (formatted === current) return prev;
      markRowAsEdited(index); // idempotent Set.add, safe inside the updater
      const arr = [...prev];
      arr[index] = { ...arr[index], phone: formatted };
      return arr;
    });
  }, [markRowAsEdited]);

  const handleEmailChange = useCallback((e, index) => {
    markRowAsEdited(index);
    const trimmedEmail = e.target.value.trim();

    setLocalContacts((prev) => {
      const arr = [...prev];
      arr[index] = { ...arr[index], email: trimmedEmail, status: false, verifiedAt: null, verifyResult: '', verifyCode: '', verifyReason: '', isStale: false, didYouMean: '' };
      return arr;
    });

    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z0-9]{2,}$/;
    const errorMessage =
      trimmedEmail && !emailRegex.test(trimmedEmail) ? 'Invalid email format' : '';

    setLocalErrors((prev) => {
      const arr = [...prev];
      arr[index] = { ...(arr[index] || {}), email: errorMessage };
      return arr;
    });

    setShowValidateButton((prev) => {
      const arr = [...prev];
      arr[index] = trimmedEmail !== (validatedEmails[index] || '');
      return arr;
    });
  }, [validatedEmails]);

  const handleRemarksChange = useCallback((e, index) => {
    markRowAsEdited(index);
    const value = e.target.value;
    setLocalContacts((prev) => {
      const arr = [...prev];
      arr[index] = { ...arr[index], remarks: value };
      return arr;
    });
  }, [markRowAsEdited]);

  const validateEmail = useCallback(async (email, index) => {
    if (!email) return;
    if (localErrors[index]?.email) return;
    if (verifyingIndex !== null) return; // prevent double-click

    setVerifyingIndex(index);
    dispatch(setLoader(true));
    try {
      const response = await dispatch(validateEmailThunk({ mails: email })).unwrap();
      const result = response?.data?.emailValidation?.[0];
      const isDeliverable = result?.reason === 'Deliverable';

      setValidatedEmails((prev) => { const arr = [...prev]; arr[index] = email; return arr; });
      setShowValidateButton((prev) => { const arr = [...prev]; arr[index] = false; return arr; });
      setLocalContacts((prev) => {
        const arr = [...prev];
        arr[index] = {
          ...arr[index],
          status: isDeliverable,
          verifiedAt: result?.verifiedAt || new Date().toISOString(),
          verifyResult: result?.result || '',
          verifyCode: result?.code || '',
          verifyReason: result?.reason || '',
          isStale: result?.isStale || false,
          didYouMean: result?.did_you_mean || '',
        };
        return arr;
      });
      // Check suppression/unsubscribe list
      try {
        const { data: suppData } = await axios.get(`${SERVER_URL}/api/ses/check-suppression`, { params: { email } });
        if (suppData?.suppressed) {
          const reason = suppData.type === 'unsubscribed' ? 'This contact has unsubscribed' : `Suppressed: ${suppData.reason || suppData.type}`;
          message.warning(reason, 5);
        }
      } catch { /* non-fatal */ }

      if (isDeliverable) {
        message.success('Email is deliverable');
      } else {
        message.warning(`Validation result: ${result?.result || 'Unknown'} — ${result?.reason || ''}`);
      }
      if (!isDeliverable) {
        setLocalErrors((prev) => {
          const arr = [...prev];
          arr[index] = { email: `${result?.reason || 'Not deliverable'}` };
          return arr;
        });
      } else {
        setLocalErrors((prev) => { const arr = [...prev]; arr[index] = {}; return arr; });
      }
      // Auto-save verification to DB (no full lead save needed)
      if (values?.id) {
        dispatch(updateContactVerificationThunk({
          leadId: values.id,
          email,
          status: isDeliverable,
          verifiedAt: result?.verifiedAt || new Date().toISOString(),
          verifyResult: result?.result || '',
          verifyCode: result?.code || '',
          verifyReason: result?.reason || '',
        })).catch(() => {});
        // Log verification activity
        axios.post(`${SERVER_URL}/api/ses/log-verification`, {
          email,
          leadId: values.id,
          leadDisplayId: values.leadID || '',
          leadName: values.agencyName || '',
          contactName: localContacts[index]?.contactPerson || '',
          verifiedBy: loginUser?.id || '',
          verifiedByName: loginUser ? `${loginUser.firstname} ${loginUser.lastname}` : '',
          code: result?.code || '',
          result: result?.result || '',
          reason: result?.reason || '',
          isValid: isDeliverable,
          source: 'lead',
        }).catch(() => {});
      }
    } catch (err) {
      message.error('Failed to validate email');
    }
    setVerifyingIndex(null);
    dispatch(setLoader(false));
  }, [dispatch, localErrors, values?.id, verifyingIndex]);

  const reVerifyEmail = useCallback(async (email, index) => {
    if (!email) return;
    if (verifyingIndex !== null) return;
    setVerifyingIndex(index);
    dispatch(setLoader(true));
    try {
      const response = await dispatch(reVerifyEmailThunk({ email })).unwrap();
      const result = response?.data?.reVerifyEmail;
      const isDeliverable = result?.reason === 'Deliverable';

      setLocalContacts((prev) => {
        const arr = [...prev];
        arr[index] = {
          ...arr[index],
          status: isDeliverable,
          verifiedAt: result?.verifiedAt || new Date().toISOString(),
          verifyResult: result?.result || '',
          verifyCode: result?.code || '',
          isStale: false,
          didYouMean: result?.did_you_mean || '',
        };
        return arr;
      });
      setShowValidateButton((prev) => { const arr = [...prev]; arr[index] = false; return arr; });
      message.success(`Re-verified: ${result?.result || 'Done'}`);
      // Auto-save to DB
      if (values?.id) {
        dispatch(updateContactVerificationThunk({
          leadId: values.id,
          email,
          status: isDeliverable,
          verifiedAt: result?.verifiedAt || new Date().toISOString(),
          verifyResult: result?.result || '',
          verifyCode: result?.code || '',
          verifyReason: result?.reason || '',
        })).catch(() => {});
        axios.post(`${SERVER_URL}/api/ses/log-verification`, {
          email,
          leadId: values.id,
          leadDisplayId: values.leadID || '',
          leadName: values.agencyName || '',
          contactName: localContacts[index]?.contactPerson || '',
          verifiedBy: loginUser?.id || '',
          verifiedByName: loginUser ? `${loginUser.firstname} ${loginUser.lastname}` : '',
          code: result?.code || '',
          result: result?.result || '',
          reason: result?.reason || '',
          isValid: isDeliverable,
          source: 're-verify',
        }).catch(() => {});
      }
    } catch (err) {
      message.error(err || 'Re-verification failed');
    }
    setVerifyingIndex(null);
    dispatch(setLoader(false));
  }, [dispatch, values?.id, verifyingIndex]);

  const handleDeleteRow = useCallback((index, arrayHelpers) => {
    Swal.fire({
      title: 'Are you sure?',
      text: 'Do you really want to delete this Contact?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel',
    }).then((result) => {
      if (result.isConfirmed) {
        setLocalContacts((prev) => prev.filter((_, i) => i !== index));
        setValidatedEmails((prev) => prev.filter((_, i) => i !== index));
        setLocalErrors((prev) => prev.filter((_, i) => i !== index));
        setShowValidateButton((prev) => prev.filter((_, i) => i !== index));

        // Update the ref to account for deleted index
        const newRef = new Set();
        userEditedIndicesRef.current.forEach((idx) => {
          if (idx < index) {
            newRef.add(idx);
          } else if (idx > index) {
            newRef.add(idx - 1);
          }
        });
        userEditedIndicesRef.current = newRef;

        if (arrayHelpers) {
          arrayHelpers.remove(index);
        }
      }
    });
  }, []);

  // ✅ OPTIMIZED: Debounced Formik sync (100ms)
  useEffect(() => {
    if (syncTimeoutRef.current) {
      clearTimeout(syncTimeoutRef.current);
    }

    syncTimeoutRef.current = setTimeout(() => {
      setFieldValue('leadsContacts', localContacts || [], false);
    }, 100);

    return () => {
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
      }
    };
  }, [localContacts, setFieldValue]);

  return (
    <FieldArray
      name="leadsContacts"
      render={(arrayHelpers) => (
        <div>
          {localContacts?.map((contact, index) => (
            <ContactRow
              key={`contact-${index}`}
              contact={contact}
              index={index}
              errors={errors}
              touched={touched}
              localErrors={localErrors}
              validatedEmails={validatedEmails}
              showValidateButton={showValidateButton}
              hasRingCentralIframe={hasRingCentralIframe}
              callingTrackRef={callingTrackRef}
              onContactPersonChange={handleContactPersonChange}
              onTitleChange={handleTitleChange}
              onPhoneChange={handlePhoneChange}
              onPhoneBlur={handlePhoneBlur}
              onEmailChange={handleEmailChange}
              onRemarksChange={handleRemarksChange}
              showSidePanel={showSidePanel}
              onValidateEmail={validateEmail}
              onReVerifyEmail={reVerifyEmail}
              verifyingIndex={verifyingIndex}
              onDeleteRow={(idx) => handleDeleteRow(idx, arrayHelpers)}
              setLocalContacts={setLocalContacts}
              markRowAsEdited={markRowAsEdited}
              onUnsubscribe={(contact, idx) => {
                Swal.fire({
                  title: 'Unsubscribe Contact?',
                  text: `This will mark ${contact.email} as unsubscribed, stop all active campaigns and sequences, and prevent future emails.`,
                  icon: 'warning',
                  showCancelButton: true,
                  confirmButtonText: 'Yes, Unsubscribe',
                  confirmButtonColor: '#dc2626',
                }).then((result) => {
                  if (result.isConfirmed) {
                    dispatch(manualUnsubscribe({ contactEmail: contact.email, leadId: values?._id || values?.id })).then((res) => {
                      if (!res.error) {
                        message.success(`${contact.email} unsubscribed`);
                        setLocalContacts((prev) => {
                          const arr = [...prev];
                          arr[idx] = { ...arr[idx], optIn: false, unsubscribedAt: new Date().toISOString() };
                          return arr;
                        });
                      }
                    });
                  }
                });
              }}
            />
          ))}

          <Row
            justify="end"
            style={{ marginTop: "-16px", marginBottom: "-30px", marginRight: "15px" }}
          >
            <AntdButton
              // label="Add Row"
              label={<FontAwesomeIcon icon={faPlus} />}   // ⬅️ FontAwesome + icon
              type="primary"
              className="add-row-small-btn"
              onClick={() => {
                const newContact = {
                  contactPerson: '',
                  title: '',
                  phone: '',
                  email: '',
                  remarks: '',
                  optIn: false,
                  status: false,
                };
                setLocalContacts((prev) => [...prev, newContact]);
                setValidatedEmails((prev) => [...prev, '']);
                setLocalErrors((prev) => [...prev, {}]);
                setShowValidateButton((prev) => [...prev, false]);
                // Mark as edited since user created it
                markRowAsEdited(localContacts.length);
                handleAddRow(arrayHelpers);
              }}
            />
          </Row>
        </div>
      )}
    />
  );
};

export default ContactInformationForm;